import { useState } from 'react';
import { useToast } from 'components/toast/ToastProvider';
import { useRejectionReasons } from 'features/orders/hooks/useRejectOrder';
import { useVendorNotifications } from '../hooks/useVendorNotifications';
import { NotificationCard } from './NotificationCard';
import { RejectNotificationModal } from './RejectNotificationModal';

/**
 * Dashboard panel of actionable vendor requests (price checks / inventory).
 * Renders nothing when there are no requests.
 */
export function NotificationsPanel() {
  const { notifications, refetch, accept, reject, ignoreOne } = useVendorNotifications();
  const { reasons } = useRejectionReasons();
  const toast = useToast();

  const [rejecting, setRejecting] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  if (notifications.length === 0) return null;

  const handleAccept = async (notification, { price, qty }) => {
    const vendorPrice = parseFloat(price);
    if (!(vendorPrice > 0)) {
      toast.error('Enter a valid price', 'Price must be greater than 0.');
      return;
    }
    setBusyId(notification.id);
    try {
      const result = await accept({
        notificationId: notification.notificationId || notification.id,
        vendorPrice,
        vendorCatalogId: notification.vendorCatalogId,
        quantity: parseFloat(qty) || 1,
        productId: notification.productId,
      });
      if (result?.success) {
        toast.success('Added to catalog', result.message);
        refetch();
      } else {
        toast.error('Could not accept', result?.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleIgnore = async (notification) => {
    setBusyId(notification.id);
    try {
      const result = await ignoreOne(notification.notificationId || notification.id);
      if (result?.success === false) toast.error('Could not ignore', result?.message);
      refetch();
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmReject = async (reasonValue) => {
    if (!reasonValue || !rejecting) return;
    setRejectLoading(true);
    try {
      const result = await reject({
        productId: rejecting.productId,
        rejectionReason: reasonValue,
        notificationId: rejecting.notificationId || rejecting.id,
      });
      if (result?.success) {
        toast.success('Marked unavailable', result.message);
        setRejecting(null);
        refetch();
      } else {
        toast.error('Could not submit', result?.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Requests</h2>
        <span className="text-xs text-slate-500">{notifications.length}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            busy={busyId === notification.id}
            onAccept={(values) => handleAccept(notification, values)}
            onReject={() => setRejecting(notification)}
            onIgnore={() => handleIgnore(notification)}
          />
        ))}
      </div>

      <RejectNotificationModal
        key={rejecting?.id}
        notification={rejecting}
        reasons={reasons}
        loading={rejectLoading}
        onConfirm={handleConfirmReject}
        onClose={() => setRejecting(null)}
      />
    </section>
  );
}

export default NotificationsPanel;
